import { useMemo, useState } from "react";
import PageHero from "@/components/feature/PageHero";
import SectionHeading from "@/components/base/SectionHeading";
import Pagination from "@/components/base/Pagination";
import ReviewCard from "@/components/feature/ReviewCard";
import useSeo from "@/hooks/useSeo";
import { reviews, reviewCategories, sortOptions } from "@/mocks/reviews";

const PAGE_SIZE = 10;

export default function Reviews() {
  useSeo({
    title: "激情丛林电影影评 - 专业影评与观众声音合集",
    description:
      "《激情丛林》影评合集：收录专业影评人点评、剧情深度解析与真实观众声音。支持按评分与时间排序、按类型筛选，帮助影迷全面了解影片口碑。",
    keywords: "激情丛林影评,电影影评,观众评价,影评解析,电影口碑",
  });

  const [category, setCategory] = useState("全部");
  const [sort, setSort] = useState("rating_desc");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = reviews.filter((r) => category === "全部" || r.category === category);
    return [...list].sort((a, b) => {
      switch (sort) {
        case "rating_asc":
          return a.rating - b.rating;
        case "date_desc":
          return a.date < b.date ? 1 : -1;
        case "date_asc":
          return a.date > b.date ? 1 : -1;
        default:
          return b.rating - a.rating;
      }
    });
  }, [category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetAll = () => {
    setCategory("全部");
    setSort("rating_desc");
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHero
        title="影片影评与观众声音"
        subtitle="从专业影评人的影像分析，到普通观众走出影院后的真实感受，这里汇集了关于《激情丛林》的多元声音。"
        crumbs={[{ label: "影评" }]}
      />

      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
        <SectionHeading
          eyebrow="Reviews"
          title="全部影评"
          desc={`当前共收录 ${filtered.length} 篇影评`}
        />

        <div className="panel mb-8 flex flex-col gap-4 rounded-lg p-4 md:p-5">
          <div className="flex flex-wrap items-center gap-2">
            {reviewCategories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setCategory(c);
                  setPage(1);
                }}
                className={`whitespace-nowrap rounded-full px-4 py-2 font-label text-sm transition-colors ${
                  category === c
                    ? "bg-primary-500 font-semibold text-background-50"
                    : "border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-accent-400"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 border-t border-background-800 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="flex items-center gap-2 font-label text-xs text-foreground-500">
              <i className="ri-filter-3-line"></i>
              筛选与排序
            </span>
            <div className="flex items-center gap-3">
              <label
                htmlFor="review-sort"
                className="font-label text-xs text-foreground-500 sm:hidden"
              >
                排序
              </label>
              <select
                id="review-sort"
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1);
                }}
                className="cursor-pointer rounded-md border border-background-700 bg-background-900 px-3 py-2 font-label text-sm text-foreground-100 focus:border-primary-500 focus:outline-none"
              >
                {sortOptions.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="flex flex-col gap-4">
              {pageItems.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
            <Pagination current={safePage} total={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="panel flex flex-col items-center justify-center rounded-lg px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-400">
              <i className="ri-emotion-unhappy-line text-3xl"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-bold text-foreground-50">
              没有找到符合条件的影评
            </h3>
            <p className="mt-2 max-w-md text-sm text-foreground-400">
              试着调整分类或排序条件，或者重置筛选查看全部内容。
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-semibold text-background-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </section>
    </div>
  );
}